import { imageUrl } from 'util/images';

export const FULL_VIEWPORT = {
  value: 100,
  units: 'vw'
};

// The image used will always be within 1 interval of width from the rendered
// width on the page.
export const WIDTH_INCREMENT = 100; // px

// assume images will never be smaller than full-width on mobile
export const MIN_WIDTH = 320; // px

export const HI_DPI_FACTOR = 2; // multiplier
export const MID_DPI_FACTOR = 1.5; // multiplier for small teaser images

export const MIN_HERO_WIDTH = 600;

// https://developer.mozilla.org/en-US/docs/Web/CSS/length#Units
export const FIXED_SIZE_UNITS = ['px', 'cm', 'mm', 'in', 'pc', 'pt'];

/**
 * returns true if the specified unit an fixed (absolute) size; returns false if
 * unit is relative.
 *
 * @param {String} unit unit name
 * @return {Boolean} true if unit is fixed size; false if not
 */
export function isFixedSize(unit) {
  return FIXED_SIZE_UNITS.includes(unit);
}

/**
 * returns width and a calculated rounded-up height if an aspect ratio is
 * passed. returns width only if no aspect ratio is passed.
 *
 * @param {Number} width
 * @param {Number} aspectRatio width / height without any rounding
 * @returns {Object} dimensions object
 */
export function getDimensions(width, aspectRatio) {
  return {
    width,
    ...(aspectRatio && {
      height: Math.ceil(width / aspectRatio)
    })
  };
}

/**
 * creates a list of values that describes images for the img srcset attribute
 * based on pixel densities.
 *
 * [
 *  'https://host/img.png?width=400&height=267 2x',
 *  'https://host/img.png?width=300&height=200 1.5x',
 *  'https://host/img.png?width=200&height=134 1x'
 * ]
 *
 * @export
 * @param {String} url base image URL
 * @param {Object} renderOps render options
 * @param {Number} width maximum width, in pixels
 * @param {Number} aspectRatio width/height as Float
 * @returns {String} list of resolution entries separated by commas
 */

export function generateResolutionList(url, renderOps, width, aspectRatio) {
  // Choosing an arbitrary cut-off for higher res images
  const isHero = width > MIN_HERO_WIDTH;
  const multiplier = isHero ? HI_DPI_FACTOR : MID_DPI_FACTOR;
  const offset = isHero ? 1 : 0.5;
  const sources = [];

  for (let i = 1; i <= multiplier; i += offset) {
    const dimensions = getDimensions(Math.ceil(width * i), aspectRatio);
    sources.unshift(`${imageUrl(url, { ...renderOps, ...dimensions })} ${i}x`);
  }

  return sources.join(',');
}

/**
 * Creates a list of values that describes images for the img srcset attribute
 * based on width. This generates sizes using a maximum width and a width increment.
 * NOTE: supports relative sizes
 *
 * [
 *  'https://host/img.png?width=500&height=334 500w',
 *  'https://host/img.png?width=400&height=267 400w'
 * ]
 *
 * @export
 * @param {String} url base image URL
 * @param {Object} renderOps image render ops
 * @param {Number} maxWidth maximum width, in pixels
 * @param {Number} aspectRatio width/height as float
 * @returns {String} list of width descriptor entries separated by commas
 */
export function generateWidthList(url, renderOps, maxWidth, aspectRatio) {
  const sources = [];
  // Force width to be an integer
  let width = Math.ceil(maxWidth * HI_DPI_FACTOR);

  do {
    const ops = { ...renderOps, ...getDimensions(width, aspectRatio) };
    sources.push(`${imageUrl(url, ops)} ${width}w`);
    width = width - WIDTH_INCREMENT; // eslint-disable-line operator-assignment
  } while (width >= MIN_WIDTH);

  return sources.join(',');
}

/**
 * Creates a list of values that describes images for the img srcset attribute
 * based on width. This generates an absolute list using the fixed widht value.
 * NOTE: does not support relative sizes
 *
 * [
 *  'https://host/img.png?width=500&height=334 500w',
 *  'https://host/img.png?width=400&height=267 400w'
 * ]
 *
 * @export
 * @param {String} url base image URL
 * @param {Object} sourceSizes render
 * @param {Object} renderOps image render ops
 * @param {Number} aspectRatio width/height as float
 * @returns {String} list of width descriptor entries separated by commas
 */

export function generatePixelList(url, sourceSizes, renderOps, aspectRatio) {
  const set = sourceSizes.map(({ layoutWidth }) => {
    const { value: width } = layoutWidth;
    const sources = [];
    for (let i = 1; i <= HI_DPI_FACTOR; i += 1) {
      const dimensions = getDimensions(width * i, aspectRatio);
      sources.unshift(
        `${imageUrl(url, { ...renderOps, ...dimensions })} ${width * i}w`
      );
    }
    return sources;
  });
  // Flatten and join
  return set.reduce((acc, val) => acc.concat(val), []).join(',');
}

/**
 * returns a comma-delimited list of media queries and an approximate width for
 * the image at that breakpoint. widths are expressed as any standard CSS unit.
 * used in <source /> and <img/ >.
 *
 * if no media query is present, then only the width is output (for <img />)
 *
 * @export
 * @param {Object[]} sourceSizes specs for widths of an image across media queries
 * @param {String} sourceSizes[].media media query
 * @param {String} sourceSizes[].layoutWidth value & units
 * @returns {String} comma-separated values for sizes attr
 */
export function buildSizes(sourceSizes) {
  // CLEANUP: how to verify that only the last item has no media query?
  return sourceSizes
    .map(({ media, layoutWidth: { value, units } }) => {
      const optMq = media ? `${media} ` : '';
      return `${optMq}${value}${units}`;
    })
    .join(',');
}
